/**
 * ModulePad - macOS Launchpad-style Module Grid Overlay
 *
 * 상단: Noten 섹션 (히어로 카드)
 * 하단: 수업 관리 3x4 그리드
 *
 * @module ModulePad
 */

import { useEffect, useCallback, type ReactNode } from 'react';
import { useScrollLock } from '~/core/hooks/useScrollLock';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays,
  Users,
  BookOpen,
  BookText,
  MessageSquare,
  GraduationCap,
  BarChart2,
  Pin,
  PinOff,
  Lock,
  Layers,
  NotebookPen,
  School,
  ClipboardList,
  FileQuestion,
  Sparkles,
} from 'lucide-react';
import { TimelineIcon } from '~/modules/icons/TimelineIcon';
import { FlowsIcon } from '~/modules/icons/FlowsIcon';
import { cn } from '~/core/utils/cn';
import { ECOSYSTEM_MODULES, TEACHING_MODULES, type ModuleDefinition } from '~/core/config/modules';
import {
  useGlobalStore,
  useIsModulePadOpen,
  useCloseModulePad,
  usePinnedModules,
  usePinModule,
  useUnpinModule,
  type AppModule,
} from '~/core/store/useGlobalStore';

// ============================================================================
// ICON MAP
// ============================================================================

const MODULE_ICONS: Record<string, ReactNode> = {
  Clock: <TimelineIcon className="w-6 h-6" />,
  CalendarDays: <CalendarDays className="w-6 h-6" />,
  Users: <Users className="w-6 h-6" />,
  Flows: <FlowsIcon className="w-6 h-6" />,
  BookOpen: <BookOpen className="w-6 h-6" />,
  BookText: <BookText className="w-6 h-6" />,
  MessageSquare: <MessageSquare className="w-6 h-6" />,
  GraduationCap: <GraduationCap className="w-6 h-6" />,
  BarChart2: <BarChart2 className="w-6 h-6" />,
  Layers: <Layers className="w-6 h-6" />,
  NotebookPen: <NotebookPen className="w-6 h-6" />,
  School: <School className="w-6 h-6" />,
  ClipboardList: <ClipboardList className="w-6 h-6" />,
  FileQuestion: <FileQuestion className="w-6 h-6" />,
  Sparkles: <Sparkles className="w-6 h-6" />,
};

function getModuleIcon(iconName: string): ReactNode {
  return MODULE_ICONS[iconName] ?? null;
}

// ============================================================================
// ANIMATION
// ============================================================================

const cardVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 16 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.85, y: 8 },
};

// ============================================================================
// SHARED: PIN TOGGLE
// ============================================================================

function PinToggle({ module, isPinned, onTogglePin }: {
  module: ModuleDefinition;
  isPinned: boolean;
  onTogglePin: (e: React.MouseEvent, m: ModuleDefinition) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={-1}
      onClick={(e) => onTogglePin(e, module)}
      className={cn(
        'absolute top-2 right-2 p-1 rounded-md transition-all duration-200',
        module.isRequired
          ? 'opacity-30 cursor-not-allowed'
          : isPinned
            ? 'opacity-70 hover:opacity-100 hover:bg-error-tint hover:scale-110'
            : 'opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:bg-white/15 hover:scale-110',
      )}
      title={module.isRequired ? '필수 모듈' : isPinned ? '독에서 해제' : '독에 고정'}
    >
      {module.isRequired ? (
        <Lock className="w-3 h-3 text-white/60" />
      ) : isPinned ? (
        <PinOff className="w-3 h-3 text-error-vivid" />
      ) : (
        <Pin className="w-3 h-3 text-white" />
      )}
    </div>
  );
}

// ============================================================================
// HERO MODULE CARD (Noten)
// ============================================================================

interface CardProps {
  module: ModuleDefinition;
  isPinned: boolean;
  isCurrent: boolean;
  index: number;
  onNavigate: (id: string) => void;
  onTogglePin: (e: React.MouseEvent, m: ModuleDefinition) => void;
}

function HeroModuleCard({ module, isPinned, isCurrent, index, onNavigate, onTogglePin }: CardProps) {
  return (
    <motion.button
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ type: 'spring', stiffness: 400, damping: 28, delay: index * 0.03 }}
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        'group relative flex items-center gap-4 px-4 py-3 cursor-pointer w-full text-left',
        'rounded-xl border border-white/[0.10] backdrop-blur-md',
        'hover:border-white/[0.18] transition-colors duration-150',
        'select-none outline-none',
        isCurrent ? 'ring-1 ring-white/20 bg-white/[0.12]' : 'bg-white/[0.06] hover:bg-white/[0.10]',
      )}
      onClick={() => onNavigate(module.id)}
      tabIndex={-1}
    >
      {isCurrent && <div className="absolute top-2.5 left-2.5 w-1.5 h-1.5 rounded-full bg-white/60" />}
      <PinToggle module={module} isPinned={isPinned} onTogglePin={onTogglePin} />

      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 shadow-md"
        style={{
          background: `linear-gradient(135deg, var(--accent-color), color-mix(in srgb, var(--accent-color) 70%, black))`,
          color: 'var(--accent-text, #fff)',
        }}
      >
        {getModuleIcon(module.iconName)}
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-semibold text-white/90 leading-tight">{module.label}</span>
        <span className="text-[11px] text-white/40 leading-snug">{module.description}</span>
      </div>
    </motion.button>
  );
}

// ============================================================================
// MODULE CARD (수업 관리 그리드)
// ============================================================================

function ModuleCard({ module, isPinned, isCurrent, index, onNavigate, onTogglePin }: CardProps) {
  return (
    <motion.button
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ type: 'spring', stiffness: 400, damping: 28, delay: index * 0.03 }}
      whileHover={{ scale: 1.04, y: -2 }}
      whileTap={{ scale: 0.97 }}
      className={cn(
        'group relative flex flex-col items-center gap-2 p-4 pb-3 cursor-pointer',
        'rounded-xl border border-white/[0.08]',
        'bg-white/[0.06] backdrop-blur-md',
        'hover:bg-white/[0.12] hover:border-white/[0.15]',
        'transition-colors duration-150',
        'select-none outline-none',
        isCurrent && 'ring-1 ring-white/20 bg-white/[0.10]',
      )}
      onClick={() => onNavigate(module.id)}
      tabIndex={-1}
    >
      {isCurrent && <div className="absolute top-2 left-2 w-1.5 h-1.5 rounded-full bg-white/60" />}
      <PinToggle module={module} isPinned={isPinned} onTogglePin={onTogglePin} />

      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center shadow-md"
        style={{
          background: `linear-gradient(135deg, var(--accent-color), color-mix(in srgb, var(--accent-color) 70%, black))`,
          color: 'var(--accent-text, #fff)',
        }}
      >
        {getModuleIcon(module.iconName)}
      </div>
      <span className="text-xs font-semibold text-white/90 text-center leading-tight">
        {module.label}
      </span>
    </motion.button>
  );
}

// ============================================================================
// MODULE PAD
// ============================================================================

export function ModulePad() {
  const isOpen = useIsModulePadOpen();
  const closeModulePad = useCloseModulePad();
  const pinnedModules = usePinnedModules();
  const pinModule = usePinModule();
  const unpinModule = useUnpinModule();
  const setCurrentModule = useGlobalStore((s) => s.setCurrentModule);
  const currentModule = useGlobalStore((s) => s.currentModule);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) closeModulePad(); },
    [isOpen, closeModulePad]
  );
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useScrollLock(isOpen);

  const handleNavigate = useCallback(
    (moduleId: string) => { setCurrentModule(moduleId as AppModule); closeModulePad(); },
    [setCurrentModule, closeModulePad]
  );

  const handleTogglePin = useCallback(
    (e: React.MouseEvent, module: ModuleDefinition) => {
      e.stopPropagation();
      if (module.isRequired) return;
      pinnedModules.includes(module.id) ? unpinModule(module.id) : pinModule(module.id);
    },
    [pinnedModules, pinModule, unpinModule]
  );

  if (typeof window === 'undefined') return null;

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[200] scrim-strong"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeModulePad}
            aria-hidden="true"
          />

          <motion.div
            className="fixed inset-0 z-[201] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeModulePad}
          >
            <div
              className="w-full max-w-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <motion.div
                className="text-center mb-6"
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05, type: 'spring', stiffness: 300, damping: 30 }}
              >
                <h2 className="text-lg font-bold text-white/90 tracking-tight">모듈 패드</h2>
                <p className="text-[10px] text-white/30 mt-1">클릭하여 이동 · ESC로 닫기</p>
              </motion.div>

              {/* Noten */}
              <div className="mb-4">
                <h3 className="text-[10px] font-semibold text-white/35 mb-2 tracking-widest">Note'n</h3>
                <div className="flex flex-col gap-2">
                  {ECOSYSTEM_MODULES.map((m, i) => (
                    <HeroModuleCard key={m.id} module={m} isPinned={pinnedModules.includes(m.id)} isCurrent={currentModule === m.id} index={i} onNavigate={handleNavigate} onTogglePin={handleTogglePin} />
                  ))}
                </div>
              </div>

              {/* 수업 관리 */}
              <div>
                <h3 className="text-[10px] font-semibold text-white/35 mb-2 tracking-widest uppercase">수업 관리</h3>
                <div className="grid grid-cols-3 gap-2.5">
                  {TEACHING_MODULES.map((m, i) => (
                    <ModuleCard key={m.id} module={m} isPinned={pinnedModules.includes(m.id)} isCurrent={currentModule === m.id} index={i} onNavigate={handleNavigate} onTogglePin={handleTogglePin} />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
